import { JobV2 } from "../@types/job";
import { Currency } from "../@types/client";
import { isQbToBillInvoiceJob } from "../_constants/tags";

// Rules for moving an existing job to a different client. Mirrors the
// client currency-change guard: allowed only while nothing financial has
// happened on the job.
//
//  1. No payments on the job. QuickBooks refuses to change the customer on
//     an invoice with a payment applied, and receipts have already gone out
//     under the old client's name. Deposits and refunded-to-zero both count.
//  2. Same currency. QuickBooks invoices are single-currency and the job's
//     prices were entered in the old client's currency.
//  3. A QuickBooks-synced job can only move to a QuickBooks-synced client,
//     otherwise the invoice has no customer to move to.
//  4. QuickBooks-billed jobs never move. MountMonitor only links to the
//     invoice QuickBooks owns, so the client lives there.

export interface JobClientSwitchCandidate {
  uid: string;
  firstName?: string;
  lastName?: string;
  currency?: Currency | null;
  syncedToAccountingIntegration?: boolean;
}

/** Why the client on this job can't be changed at all, or null if it can. */
export function jobClientLockedReason(job: JobV2): string | null {
  if (job.payments?.length > 0) {
    return "The client can't be changed once a payment has been taken on this job.";
  }
  if (isQbToBillInvoiceJob(job.tags)) {
    return "QuickBooks bills this invoice, so the client is managed in QuickBooks.";
  }
  return null;
}

/**
 * Why this job can't move to `candidate`, or null if it can. `jobSynced` is
 * the sync flag the job will be saved with (the form can toggle it on).
 */
export function jobClientSwitchBlockedReason(
  job: JobV2,
  candidate: JobClientSwitchCandidate,
  jobSynced: boolean
): string | null {
  if (candidate.uid === job.client?.uid) {
    return null;
  }

  const locked = jobClientLockedReason(job);
  if (locked) {
    return locked;
  }

  const jobCurrency = job.client?.currency?.code;
  const candidateCurrency = candidate.currency?.code;
  if (jobCurrency && candidateCurrency && jobCurrency !== candidateCurrency) {
    const name = [candidate.firstName, candidate.lastName]
      .filter(Boolean)
      .join(" ");
    return `${name || "That client"} is billed in ${candidateCurrency} but this job is in ${jobCurrency}. Only a client with the same currency can take this job.`;
  }

  if (jobSynced && !candidate.syncedToAccountingIntegration) {
    return "This job is synced to QuickBooks, so it can only move to a client who is also synced to QuickBooks.";
  }

  return null;
}
